/**
 * 技能预览模态框
 *
 * 显示技能的完整内容，包括：
 * - YAML frontmatter
 * - Markdown 内容
 * - 技能元数据
 */

import React, { useState, useEffect } from 'react';
import { skillApi } from '../../services/skillApi';
import type { SkillDetail } from '../../types/skill';

interface SkillPreviewModalProps {
  /** 是否打开 */
  isOpen: boolean;
  /** 技能名称 */
  skillName: string | null;
  /** 关闭回调 */
  onClose: () => void;
}

export const SkillPreviewModal: React.FC<SkillPreviewModalProps> = ({
  isOpen,
  skillName,
  onClose,
}) => {
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 加载技能内容
  useEffect(() => {
    if (!isOpen || !skillName) {
      setSkill(null);
      return;
    }

    const fetchSkill = async () => {
      setLoading(true);
      setError(null);
      try {
        const skillData = await skillApi.getSkill(skillName);
        setSkill(skillData);
      } catch (err) {
        setError('加载技能内容失败');
        console.error('加载技能内容失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSkill();
  }, [isOpen, skillName]);

  // 按ESC关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {skill?.name || skillName || '技能预览'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 加载中状态 */}
        {loading && (
          <div className="py-8 text-center text-gray-600">
            加载技能内容...
          </div>
        )}

        {/* 错误状态 */}
        {error && (
          <div className="py-8 text-center text-red-600">
            {error}
          </div>
        )}

        {/* 技能内容 */}
        {skill && !loading && !error && (
          <>
            {/* 元数据 */}
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
              {skill.is_system && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  系统技能
                </span>
              )}
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                {skill.source}
              </span>
              {skill.description && (
                <span className="text-gray-600">{skill.description}</span>
              )}
            </div>

            {/* 内容 */}
            <div className="flex-1 overflow-y-auto">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm text-gray-800 whitespace-pre-wrap font-mono">
                {skill.content}
              </pre>
            </div>
          </>
        )}

        {/* 关闭按钮 */}
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillPreviewModal;
